import { createSelector } from '@reduxjs/toolkit'

import {
    SELF_ASSESSMENT_STATUS_COMPLETED_NOT_FILED,
    SELF_ASSESSMENT_STATUS_FILED,
    SELF_ASSESSMENT_STATUS_FILED_AWAITING_REFUND,
    SELF_ASSESSMENT_STATUS_SUBMITTED,
} from '@/constants'
import { isSelfAssessmentStatusAtLeast } from '@/helpers'
import { ExternalApis } from '@/models/profile'

import { RootState } from '../store'

export const getProfileSlice = (state: RootState) => state.profile

export const getProfile = createSelector([getProfileSlice], (profile) => {
    const externalApis = profile.externalApis || []
    const taxConsultations = profile.taxConsultations || []
    const selfAssessments = profile.selfAssessments || []
    const productBundles = profile.productBundles || []

    const hasConnections = externalApis.length > 0

    const isConnectedToHmrc = externalApis.includes(ExternalApis.Hmrc)

    const hasChangedName = !!profile.changeNameDate || !!profile.previousLastName

    const hasSelfAssessments = selfAssessments.length > 0
    const hasTaxConsultations = taxConsultations.length > 0
    const hasProductBundles = productBundles.length > 0

    const hasFiledSelfAssessments = selfAssessments.some(({ status }: { status: string }) =>
        [
            SELF_ASSESSMENT_STATUS_COMPLETED_NOT_FILED,
            SELF_ASSESSMENT_STATUS_FILED_AWAITING_REFUND,
            SELF_ASSESSMENT_STATUS_FILED,
        ].includes(status)
    )

    const hasSubmittedSelfAssessments = selfAssessments.some(({ status }) =>
        isSelfAssessmentStatusAtLeast(status, SELF_ASSESSMENT_STATUS_SUBMITTED)
    )

    const isReturningSelfAssessmentCustomer = !!hasSubmittedSelfAssessments

    const paidSelfAssessments = selfAssessments.filter(({ hasPaid, payAfterFiling }) => !!hasPaid || !!payAfterFiling)
    const paidTaxConsultations = taxConsultations.filter(({ hasPaid }) => !!hasPaid)
    const paidProductBundles = productBundles.filter(({ hasPaid }) => !!hasPaid)

    const numberOfPaidServices = paidSelfAssessments.length + paidTaxConsultations.length + paidProductBundles.length
    const isReturningCustomer = !!profile.acquisitionDateUTC

    const hasAccountantAssigned = !!profile.accountantId

    const hasPaidTaxConsultations = paidTaxConsultations.length > 0

    return {
        ...profile,
        externalApis,
        fullName: `${String(profile.firstName || '').trim()} ${String(profile.lastName || '').trim()}`.trim(),
        hasAccountantAssigned,
        hasChangedName,
        hasConnections,
        hasFiledSelfAssessments,
        hasPaidTaxConsultations,
        hasSelfAssessments,
        hasSubmittedSelfAssessments,
        hasTaxConsultations,
        hasProductBundles,
        isConnectedToHmrc,
        isReturningCustomer,
        isReturningSelfAssessmentCustomer,
        numberOfPaidServices,
        selfAssessments,
        taxConsultations,
    }
})

export default getProfile
